// "Receive a load" guided flow (BUILD_SPEC.md §8.1, §11). Plan.md G20 — the
// primary purchasing-intake screen (HOME_TASKS routes "Receive a load" straight
// here). ONE receive entry point (Stuart ruling 2026-08-25, see BUILD_SPEC.md
// §8.1's dated amendment note): pick PO -> weighbridge ticket? (the ticket
// step is now a fork, not a separate screen) -> [ticket details + moisture]
// OR [docket line entry, no ticket] -> unload location -> review & post. The
// "yes" branch is unchanged from before the consolidation: one PO line per
// ticket (a documented simplification, not a schema limitation:
// goods_receipt_lines supports many lines per receipt, but a weighbridge
// ticket carries a single gross/tare/product — the real-world gatehouse case
// for Sabie sawdust and Howick timber/wax loads is one product per load; a
// multi-product load can still be entered as two separate "receive a load"
// runs against the same PO). The "no" branch reuses the ticket-less docket
// entry machinery that used to live behind its own "Receive without
// weighbridge" tab (manualReceiptLines.ts, ManualLineRow) — multi-line,
// mode-aware (kg+MC for mass lines, units for EACH/count lines) — and hands
// off to the same GoodsReceiptDraftModal for the actual review+post, exactly
// as it did before.
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Paper,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Stepper,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconTruckDelivery } from '@tabler/icons-react';
import { ApiRequestError } from '../../api/client';
import { useCanOperate, useIsAdmin } from '../../auth/permissions';
import { PageHeader } from '../../components/PageHeader';
import { isCountLine, isMassLine, MC_SOURCES, type McSource } from '@zebra/core';
import {
  centsToRand,
  formatMoneyCents,
  formatPoLineQtyBasis,
  formatQtyDisplay,
  nowForDatetimeLocal,
  PRICING_BASIS_DESCRIPTIONS,
  PRICING_BASIS_LABELS,
  subtractKg,
  vatCentsForDisplay,
} from './common';
import { computeLedgerPreview } from './ledgerPreview';
import { GoodsReceiptDraftModal } from './GoodsReceiptDraftModal';
import { ManualLineRow } from './ManualLineRow';
import { buildManualLines, EMPTY_MANUAL_LINE_INPUT, type ManualLineInput } from './manualReceiptLines';
import {
  ReceiveLoadPostError,
  type PurchaseOrderRow,
  useCreateGoodsReceipt,
  useLocations,
  usePurchaseOrder,
  usePurchaseOrders,
  useProducts,
  useReceiveLoad,
  useSuppliers,
} from './api';

type StepKey = 'pickPo' | 'ticket' | 'moisture' | 'location' | 'review';

const BASE_STEPS: StepKey[] = ['pickPo', 'ticket', 'moisture', 'location', 'review'];

const STEP_LABELS: Record<StepKey, string> = {
  pickPo: 'Pick PO',
  ticket: 'Weighbridge ticket',
  moisture: 'Moisture',
  location: 'Unload location',
  review: 'Review & post',
};

interface WizardState {
  poId: string | null;
  lineId: string | null;
  /** null = not yet chosen (forces an explicit answer before Next unlocks,
   * unless the PO has no weighable line at all — see the auto-set effect
   * below, "offer if available, or not if not"). */
  hasTicket: boolean | null;
  vehicleReg: string;
  externalTicketNo: string;
  haulier: string;
  driver: string;
  grossKg: string;
  tareKg: string;
  weighedAt: string;
  mcPct: string;
  mcSource: McSource;
  /** No-ticket branch only — one entry per PO line, mirrors the old
   * ReceiveManual screen's per-line state. */
  lineInputs: Record<string, ManualLineInput>;
  locationId: string;
  receivedAt: string;
  notes: string;
}

function initialState(): WizardState {
  return {
    poId: null,
    lineId: null,
    hasTicket: null,
    vehicleReg: '',
    externalTicketNo: '',
    haulier: '',
    driver: '',
    grossKg: '',
    tareKg: '',
    weighedAt: nowForDatetimeLocal(),
    mcPct: '',
    mcSource: 'METER',
    lineInputs: {},
    locationId: '',
    receivedAt: nowForDatetimeLocal(),
    notes: '',
  };
}

export function ReceiveLoadWizard() {
  const canOperate = useCanOperate();
  const isAdmin = useIsAdmin();
  const [active, setActive] = useState(0);
  const [state, setState] = useState<WizardState>(initialState());
  const [poSearch, setPoSearch] = useState('');
  // The draft-review modal, opened either automatically after a no-ticket
  // draft is created, or manually by an ADMIN recovering from the ticket
  // path's out-of-band-moisture post failure (see outOfBandGrId below) — same
  // modal, two entry points, one piece of state.
  const [draftModalId, setDraftModalId] = useState<string | null>(null);

  const openPos = usePurchaseOrders({ status: 'OPEN' });
  const partialPos = usePurchaseOrders({ status: 'PARTIAL' });
  const { data: suppliers } = useSuppliers();
  const { data: products } = useProducts();
  const poDetail = usePurchaseOrder(state.poId ?? undefined);
  const selectedPo = poDetail.data;
  const { data: locations } = useLocations({ siteId: selectedPo?.siteId, isActive: true });
  const receiveLoad = useReceiveLoad();
  const createGr = useCreateGoodsReceipt();

  const supplierName = useMemo(() => new Map((suppliers?.items ?? []).map((s) => [s.id, s.name])), [suppliers]);
  const productName = useMemo(() => new Map((products?.items ?? []).map((p) => [p.id, `${p.sku} — ${p.name}`])), [products]);
  const productsById = useMemo(() => new Map((products?.items ?? []).map((p) => [p.id, p])), [products]);

  const candidatePos = useMemo(() => {
    const merged = [...(openPos.data?.items ?? []), ...(partialPos.data?.items ?? [])];
    const q = poSearch.trim().toLowerCase();
    if (!q) return merged;
    return merged.filter(
      (po) => po.number.toLowerCase().includes(q) || (supplierName.get(po.supplierId) ?? '').toLowerCase().includes(q),
    );
  }, [openPos.data, partialPos.data, poSearch, supplierName]);

  const selectedLine = selectedPo?.lines.find((l) => l.id === state.lineId) ?? null;
  const hasWeighableLine = selectedPo ? selectedPo.lines.some((l) => isMassLine(l)) : false;

  function update<K extends keyof WizardState>(key: K, value: WizardState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  function updateLineInput(lineId: string, patch: Partial<ManualLineInput>) {
    setState((prev) => ({
      ...prev,
      lineInputs: { ...prev.lineInputs, [lineId]: { ...(prev.lineInputs[lineId] ?? EMPTY_MANUAL_LINE_INPUT), ...patch } },
    }));
  }

  // Auto-pick the line on a single-line PO — the common case — once its detail
  // loads. Multi-line POs leave `lineId` null so the picker in the ticket step
  // forces an explicit choice. A single EACH (count) line is deliberately NOT
  // auto-picked (or selectable at all) — the ticket branch is the weighbridge
  // path; count-only POs never offer it at all (see the effect below).
  useEffect(() => {
    if (selectedPo && selectedPo.lines.length === 1 && !state.lineId && isMassLine(selectedPo.lines[0]!)) {
      update('lineId', selectedPo.lines[0]!.id);
    }
  }, [selectedPo, state.lineId]);

  // "offer a weighbridge ticket use if available, or not if not" (Stuart
  // ruling 2026-08-25, verbatim) — default to the ticketed path whenever the
  // PO has a weighable line (the common case; preserves the pre-consolidation
  // flow's shape — no extra click before the familiar ticket fields appear),
  // default to the docket path when it doesn't (every line EACH/count — a
  // "Yes" option would never work, so it isn't offered at all: the radio
  // itself only renders when `hasWeighableLine`, below). Either way the user
  // can still change their mind via the radio when it's shown.
  useEffect(() => {
    if (selectedPo && state.hasTicket === null) {
      update('hasTicket', selectedPo.lines.some((l) => isMassLine(l)));
    }
  }, [selectedPo, state.hasTicket]);

  function selectPo(po: PurchaseOrderRow) {
    setState({ ...initialState(), poId: po.id });
  }

  function resetWizard() {
    setState(initialState());
    setActive(0);
    receiveLoad.reset();
    createGr.reset();
  }

  const netKg = subtractKg(state.grossKg || '0', state.tareKg || '0');
  // mcRequired must never trigger for an EACH (count) line — it has no
  // moisture basis at all (isMassLine guards this even though a count line is
  // never actually selectable in the ticket branch, see the auto-pick effect
  // + the disabled Radio option below — belt-and-braces per the goal's
  // known-defect list).
  const mcRequired = selectedLine ? isMassLine(selectedLine) && selectedLine.pricingBasis !== 'FLAT_WET' : false;

  const preview = selectedLine && isMassLine(selectedLine)
    ? computeLedgerPreview({
        basis: selectedLine.pricingBasis!,
        netKg,
        mcPct: state.mcPct || null,
        priceCentsPerTonne: selectedLine.priceCentsPerTonne,
        bands: selectedLine.bands,
      })
    : null;

  const { lines: builtLines, errors: lineErrors } = useMemo(
    () => buildManualLines(selectedPo?.lines ?? [], state.lineInputs),
    [selectedPo, state.lineInputs],
  );

  // Moisture only applies to the ticketed (single-line) branch — the no-ticket
  // branch captures moisture per line, inline in the ticket step's docket
  // table, exactly as the old ReceiveManual screen did.
  const STEPS: StepKey[] = state.hasTicket === false ? BASE_STEPS.filter((s) => s !== 'moisture') : BASE_STEPS;
  const activeKey: StepKey = STEPS[active] ?? 'pickPo';

  function canProceed(key: StepKey): boolean {
    switch (key) {
      case 'pickPo':
        return Boolean(selectedPo);
      case 'ticket':
        if (state.hasTicket === null) return false;
        if (state.hasTicket) {
          return (
            Boolean(selectedLine) &&
            state.vehicleReg.trim() !== '' &&
            state.grossKg !== '' &&
            state.tareKg !== '' &&
            Number(netKg) > 0 &&
            state.weighedAt !== ''
          );
        }
        return builtLines.length > 0 && lineErrors.size === 0;
      case 'moisture':
        return !mcRequired || state.mcPct.trim() !== '';
      case 'location':
        return state.locationId !== '';
      case 'review':
        return true;
    }
  }

  // Out-of-band moisture alone must NOT hard-block Post (mirrors
  // GoodsReceiptDraftModal's identical `hardBlocked` reasoning) — clicking
  // Post still attempts the real composite POST, which 422s
  // MOISTURE_OUT_OF_BAND, and an ADMIN then resolves it via the recovery
  // Alert below rather than being locked out before ever attempting it. Any
  // OTHER preview failure (missing price, etc.) still hard-blocks.
  const previewHardBlocked = preview !== null && !preview.ok && preview.code !== 'MOISTURE_OUT_OF_BAND';

  // Populated once `receiveLoad` fails on its LAST step (ticket+GR already
  // created as DRAFT) with an out-of-band moisture reading.
  const outOfBandGrId =
    receiveLoad.error instanceof ReceiveLoadPostError &&
    receiveLoad.error.originalError instanceof ApiRequestError &&
    receiveLoad.error.originalError.code === 'MOISTURE_OUT_OF_BAND'
      ? receiveLoad.error.grId
      : null;

  function handlePost() {
    if (!selectedPo || !selectedLine) return;
    receiveLoad.mutate({
      siteId: selectedPo.siteId,
      poId: selectedPo.id,
      poLineId: selectedLine.id,
      productId: selectedLine.productId,
      vehicleReg: state.vehicleReg.trim(),
      externalTicketNo: state.externalTicketNo.trim() || null,
      haulier: state.haulier.trim() || null,
      driver: state.driver.trim() || null,
      grossKg: state.grossKg,
      tareKg: state.tareKg,
      weighedAt: state.weighedAt,
      mcPct: state.mcPct.trim() || null,
      mcSource: state.mcPct.trim() ? state.mcSource : null,
      locationId: state.locationId,
      receivedAt: state.receivedAt,
      notes: state.notes.trim() || null,
    });
  }

  function handleCreateDocketDraft() {
    if (!selectedPo) return;
    createGr.mutate(
      {
        poId: selectedPo.id,
        ticketId: null,
        locationId: state.locationId,
        receivedAt: new Date(state.receivedAt),
        notes: state.notes.trim() || null,
        lines: builtLines,
      },
      { onSuccess: (gr) => setDraftModalId(gr.id) },
    );
  }

  if (receiveLoad.isSuccess && receiveLoad.data) {
    const posted = receiveLoad.data;
    return (
      <Paper withBorder radius="md" p="xl" maw={640}>
        <Stack gap="md" align="flex-start">
          <Badge color="green" size="lg" leftSection={<IconCheck size={14} />}>
            Posted
          </Badge>
          <Title order={2}>Load received</Title>
          <Text>{posted.summary}</Text>
          <Table.ScrollContainer minWidth={640}>
            <Table>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td>Goods receipt</Table.Td>
                  <Table.Td>{posted.goodsReceipt.number}</Table.Td>
                </Table.Tr>
                {posted.movements.map((m) => (
                  <Table.Tr key={m.movementId}>
                    <Table.Td>Movement</Table.Td>
                    <Table.Td>
                      +{formatQtyDisplay(m.qtyKg)} kg, payable {formatMoneyCents(m.payableCents)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Button leftSection={<IconTruckDelivery size={16} />} onClick={resetWizard}>
            Receive another load
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="Receive a load"
        description="Book an incoming delivery over the weighbridge — weigh in, weigh out, and the net load posts to stock automatically. No ticket? Step 2 lets you enter it from the delivery note (docket) instead."
      />
      <Stepper active={active} onStepClick={setActive} allowNextStepsSelect={false}>
        {STEPS.map((key) => (
          <Stepper.Step key={key} label={STEP_LABELS[key]} />
        ))}
      </Stepper>

      {activeKey === 'pickPo' && (
        <Stack gap="sm">
          <TextInput
            placeholder="Search by PO number or supplier…"
            value={poSearch}
            onChange={(e) => setPoSearch(e.currentTarget.value)}
          />
          <Paper withBorder radius="md">
            <Table.ScrollContainer minWidth={640}>
              <Table striped highlightOnHover verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Number</Table.Th>
                    <Table.Th>Supplier</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {candidatePos.map((po) => (
                    <Table.Tr key={po.id} bg={po.id === state.poId ? 'var(--mantine-color-graphite-1)' : undefined}>
                      <Table.Td>{po.number}</Table.Td>
                      <Table.Td>{supplierName.get(po.supplierId) ?? po.supplierId}</Table.Td>
                      <Table.Td>
                        <Badge color={po.status === 'OPEN' ? 'blue' : 'amber'}>{po.status}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Button size="xs" variant={po.id === state.poId ? 'filled' : 'light'} onClick={() => selectPo(po)}>
                          {po.id === state.poId ? 'Selected' : 'Select'}
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {candidatePos.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={4}>
                        <Text c="dimmed" ta="center" py="md">
                          No open or partial purchase orders. Create one on the Purchase orders tab first.
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>
        </Stack>
      )}

      {activeKey === 'ticket' && selectedPo && (
        <Stack gap="md" maw={640}>
          {hasWeighableLine && (
            <Radio.Group
              label="Does this load have a weighbridge ticket?"
              description='Choose "No" for docket-only deliveries (wax, packaging, consumables) that never cross the weighbridge.'
              value={state.hasTicket === null ? '' : state.hasTicket ? 'yes' : 'no'}
              onChange={(v) => update('hasTicket', v === 'yes')}
            >
              <Group gap="lg" mt="xs">
                <Radio value="yes" label="Yes — weighbridge ticket" />
                <Radio value="no" label="No — enter from the delivery note" />
              </Group>
            </Radio.Group>
          )}

          {!hasWeighableLine && (
            <Alert color="graphite" variant="light" icon={<IconAlertCircle size={16} />} title="No weighbridge line on this PO">
              <Text size="sm">
                Every line on this PO is EACH (count) — no weighbridge ticket applies. Enter what arrived from the delivery
                note below.
              </Text>
            </Alert>
          )}

          {state.hasTicket === true && (
            <Stack gap="sm">
              {selectedPo.lines.length > 1 && (
                <Radio.Group
                  label="Which PO line is this load against?"
                  description="EACH (count) lines have no weighbridge basis — not selectable here."
                  value={state.lineId ?? ''}
                  onChange={(v) => update('lineId', v)}
                >
                  <Stack gap="xs" mt="xs">
                    {selectedPo.lines.map((line) => {
                      const each = isCountLine(line);
                      return (
                        <Radio
                          key={line.id}
                          value={line.id}
                          disabled={each}
                          label={`${productName.get(line.productId) ?? line.productId} — ${formatPoLineQtyBasis(line)}`}
                          description={each ? 'No weighbridge basis for this line.' : undefined}
                        />
                      );
                    })}
                  </Stack>
                </Radio.Group>
              )}

              {selectedLine && (
                <Stack gap="sm">
                  <TextInput
                    label="Vehicle registration"
                    required
                    value={state.vehicleReg}
                    onChange={(e) => update('vehicleReg', e.currentTarget.value)}
                  />
                  <TextInput
                    label="External ticket no. (optional)"
                    value={state.externalTicketNo}
                    onChange={(e) => update('externalTicketNo', e.currentTarget.value)}
                  />
                  <Group grow>
                    <TextInput label="Haulier (optional)" value={state.haulier} onChange={(e) => update('haulier', e.currentTarget.value)} />
                    <TextInput label="Driver (optional)" value={state.driver} onChange={(e) => update('driver', e.currentTarget.value)} />
                  </Group>
                  <Group grow>
                    <TextInput label="Gross (kg)" required value={state.grossKg} onChange={(e) => update('grossKg', e.currentTarget.value)} />
                    <TextInput label="Tare (kg)" required value={state.tareKg} onChange={(e) => update('tareKg', e.currentTarget.value)} />
                  </Group>
                  <Text size="sm" c="dimmed">
                    Net: <strong>{formatQtyDisplay(netKg)} kg</strong>
                  </Text>
                  <TextInput
                    label="Weighed at"
                    type="datetime-local"
                    required
                    value={state.weighedAt}
                    onChange={(e) => update('weighedAt', e.currentTarget.value)}
                  />
                </Stack>
              )}
            </Stack>
          )}

          {state.hasTicket === false && (
            <Stack gap="sm">
              <Text fw={500} size="sm">
                Lines received — leave a line blank to skip it
              </Text>
              <Paper withBorder radius="md">
                <Table.ScrollContainer minWidth={640}>
                  <Table verticalSpacing="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Product</Table.Th>
                        <Table.Th>Received</Table.Th>
                        <Table.Th>Moisture</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {selectedPo.lines.map((line) => (
                        <ManualLineRow
                          key={line.id}
                          poLine={line}
                          productLabel={productName.get(line.productId) ?? line.productId}
                          product={productsById.get(line.productId)}
                          input={state.lineInputs[line.id] ?? EMPTY_MANUAL_LINE_INPUT}
                          onChange={(patch) => updateLineInput(line.id, patch)}
                        />
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Paper>
              {lineErrors.size > 0 && (
                <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
                  {[...lineErrors.values()].join('; ')}
                </Alert>
              )}
            </Stack>
          )}
        </Stack>
      )}

      {activeKey === 'moisture' && selectedLine && isMassLine(selectedLine) && (
        <Stack gap="sm" maw={520}>
          <Alert color="graphite" variant="light" title="Payable basis">
            <Text size="sm" fw={500}>
              {PRICING_BASIS_LABELS[selectedLine.pricingBasis ?? 'FLAT_WET']}
            </Text>
            <Text size="xs" c="dimmed">
              {PRICING_BASIS_DESCRIPTIONS[selectedLine.pricingBasis ?? 'FLAT_WET']}
            </Text>
            {selectedLine.pricingBasis === 'MOISTURE_BAND' ? (
              <Table.ScrollContainer minWidth={640}>
                <Table mt="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>MC range</Table.Th>
                      <Table.Th>Price / t</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {(selectedLine.bands ?? []).map((band, i) => (
                      <Table.Tr key={i}>
                        <Table.Td>
                          {band.mcMin}% – {band.mcMax}%
                        </Table.Td>
                        <Table.Td>R{centsToRand(band.priceCents)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            ) : (
              <Text size="sm">R{centsToRand(selectedLine.priceCentsPerTonne)} / tonne</Text>
            )}
          </Alert>
          <TextInput
            label={mcRequired ? 'Moisture content % (required for this basis)' : 'Moisture content % (optional)'}
            required={mcRequired}
            value={state.mcPct}
            onChange={(e) => update('mcPct', e.currentTarget.value)}
          />
          <Select
            label="Reading source"
            data={MC_SOURCES.map((s) => ({ value: s, label: s }))}
            value={state.mcSource}
            onChange={(v) => update('mcSource', (v as McSource) ?? 'METER')}
          />
        </Stack>
      )}

      {activeKey === 'location' && selectedPo && (
        <Stack gap="sm" maw={520}>
          <Select
            label="Unload location"
            required
            searchable
            data={(locations?.items ?? []).map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
            value={state.locationId}
            onChange={(v) => update('locationId', v ?? '')}
          />
          <TextInput
            label="Received at"
            type="datetime-local"
            required
            value={state.receivedAt}
            onChange={(e) => update('receivedAt', e.currentTarget.value)}
          />
          <Textarea
            label={state.hasTicket === false ? 'Docket / reference no. (optional)' : 'Notes (optional)'}
            autosize
            minRows={1}
            value={state.notes}
            onChange={(e) => update('notes', e.currentTarget.value)}
          />
        </Stack>
      )}

      {activeKey === 'review' && selectedPo && state.hasTicket === true && selectedLine && (
        <Stack gap="md" maw={640}>
          {receiveLoad.isError && !outOfBandGrId && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not post">
              {receiveLoad.error instanceof ApiRequestError ? receiveLoad.error.message : 'Failed to receive the load'}
            </Alert>
          )}
          {outOfBandGrId && (
            <Alert color={isAdmin ? 'amber' : 'red'} icon={<IconAlertCircle size={16} />} title="Out-of-band moisture reading">
              <Stack gap="xs">
                <Text size="sm">{receiveLoad.error?.message}</Text>
                <Text size="sm">
                  The weighbridge ticket and goods receipt were saved as a DRAFT — nothing was lost.
                  {isAdmin ? ' Open it to override the reading and post.' : ' Ask an ADMIN to override this reading, or correct it and receive the load again.'}
                </Text>
                {isAdmin && (
                  <Group justify="flex-end">
                    <Button color="amber" onClick={() => setDraftModalId(outOfBandGrId)}>
                      Open draft to override
                    </Button>
                  </Group>
                )}
              </Stack>
            </Alert>
          )}
          <SimpleGrid cols={2} spacing="sm">
            <Card withBorder padding="sm">
              <Text size="xs" c="dimmed">
                Supplier
              </Text>
              <Text fw={600}>{supplierName.get(selectedPo.supplierId) ?? selectedPo.supplierId}</Text>
            </Card>
            <Card withBorder padding="sm">
              <Text size="xs" c="dimmed">
                Product
              </Text>
              <Text fw={600}>{productName.get(selectedLine.productId) ?? selectedLine.productId}</Text>
            </Card>
            <Card withBorder padding="sm">
              <Text size="xs" c="dimmed">
                Net weight
              </Text>
              <Text fw={600}>{formatQtyDisplay(netKg)} kg</Text>
            </Card>
            <Card withBorder padding="sm">
              <Text size="xs" c="dimmed">
                Unload location
              </Text>
              <Text fw={600}>{(locations?.items ?? []).find((l) => l.id === state.locationId)?.name ?? '—'}</Text>
            </Card>
          </SimpleGrid>

          {preview && preview.ok ? (
            <Alert color="green" variant="light" title="Ledger effect preview">
              <Stack gap={4}>
                <Text size="sm">
                  Stock IN: <strong>+{formatQtyDisplay(netKg)} kg</strong> wet ({formatQtyDisplay(preview.result.dryKg)} kg dry) of{' '}
                  {productName.get(selectedLine.productId) ?? selectedLine.productId} received.
                </Text>
                <Text size="sm">
                  Payable: <strong>{formatMoneyCents(preview.result.payableCents)}</strong> (at{' '}
                  {formatMoneyCents(preview.result.usedPriceCents)}/t ex VAT)
                </Text>
                {selectedLine.vatTreatment === 'STANDARD' && (
                  <Text size="sm">VAT (15%): {formatMoneyCents(vatCentsForDisplay(preview.result.payableCents, selectedLine.vatTreatment))}</Text>
                )}
              </Stack>
            </Alert>
          ) : (
            <Alert color="amber" variant="light" icon={<IconAlertCircle size={16} />} title="Cannot post yet">
              {preview?.error ?? 'Complete the earlier steps first.'}
            </Alert>
          )}

          <Group justify="flex-end">
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button size="lg" disabled={!canOperate || !preview || previewHardBlocked} loading={receiveLoad.isPending} onClick={handlePost}>
                  Post
                </Button>
              </span>
            </Tooltip>
          </Group>
        </Stack>
      )}

      {activeKey === 'review' && selectedPo && state.hasTicket === false && (
        <Stack gap="md" maw={640}>
          {createGr.isError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not create draft">
              {createGr.error instanceof ApiRequestError ? createGr.error.message : 'Failed to create the goods receipt'}
            </Alert>
          )}

          <Paper withBorder radius="md">
            <Table.ScrollContainer minWidth={480}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Product</Table.Th>
                    <Table.Th>Received</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {builtLines.map((line) => (
                    <Table.Tr key={line.poLineId}>
                      <Table.Td>{productName.get(line.productId) ?? line.productId}</Table.Td>
                      <Table.Td>
                        {isCountLine(line)
                          ? `${formatQtyDisplay(line.qty, 0)} units`
                          : `${formatQtyDisplay(line.qtyKg)} kg${line.mcPct ? ` @ ${line.mcPct}% MC` : ''}`}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>

          <Text size="sm" c="dimmed">
            Create draft opens the same review screen every goods receipt uses, with the computed payable per line — Post from
            there.
          </Text>

          <Group justify="flex-end">
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button
                  size="lg"
                  disabled={!canOperate || builtLines.length === 0 || lineErrors.size > 0}
                  loading={createGr.isPending}
                  onClick={handleCreateDocketDraft}
                >
                  Create draft
                </Button>
              </span>
            </Tooltip>
          </Group>
        </Stack>
      )}

      <GoodsReceiptDraftModal
        grId={draftModalId}
        onClose={() => {
          setDraftModalId(null);
          resetWizard();
        }}
      />

      <Group justify="space-between">
        <Button variant="default" disabled={active === 0} onClick={() => setActive((a) => Math.max(0, a - 1))}>
          Back
        </Button>
        {active < STEPS.length - 1 && (
          <Button disabled={!canProceed(activeKey)} onClick={() => setActive((a) => Math.min(STEPS.length - 1, a + 1))}>
            Next
          </Button>
        )}
      </Group>
    </Stack>
  );
}
